import * as Msal from 'msal';
import { PagedApiResult, PageData } from '../models/utilities/paged-request';
import { RequestError } from '../models/utilities/requests';
import { CONTENT_TYPES } from '../models/utilities/type-models';
import { getApplicationStore } from '../store/configure-store';
import { AppSettings } from "./app-settings";
import { Mapper } from "./mapper";

export enum StatusCode {
    Success,
    Failure,
    Invalid
}

export class ApiResponse<T> {
    public data : T;
    public status : StatusCode = StatusCode.Failure;
}

export abstract class Api {

    /**
     * GET Api Helper that does not serialize the response at all
     * @param url The URL to hit
     */
     public static UntypedGet(url: string) : Promise<any> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
        
        const responseProm = authenticatedApiCall(fullUrl, null, 'GET', serviceCenter);
        return responseProm
                .then((resp: any) => {
                    return resp;
                })
                .catch((err) => {
                    console.error("[API UNTYPED GET] " + err);
                    throw err;
                });
    
    }

    /**
     * GET Api Helper
     * @param toClass The type to serialize the response as
     * @param url The URL to hit
     */
    public static Get<T>(toClass: new() => T, url: string) : Promise<undefined|T> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
        
        const responseProm = authenticatedApiCall(fullUrl, null, 'GET', serviceCenter);
        return responseProm
                .then((resp: any) => {
                    const result = Mapper.deserialize(toClass, resp);
                    return result as T;
                })
                .catch((err) => {
                    console.error("[API GET] " + err);
                    throw err;
                });
    
    }

    /**
     * GET Api Helper
     * @param toClass The type to serialize the response as
     * @param url The URL to hit
     */
    public static MappedGet(mapper : any, mapperProperty: string, url: string) : Promise<undefined|any> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
        
        const responseProm = authenticatedApiCall(fullUrl, null, 'GET', serviceCenter);
        return responseProm
                .then((resp: any) => {
                    const toClass = mapper(resp[mapperProperty]);
                    const result = Mapper.deserialize(toClass, resp);
                    return result;
                })
                .catch((err) => {
                    console.error("[API GET] " + err);
                    throw err;
                });
    
    }    

    /**
     * GET Api Helper for Paged Responses
     * @param toClass The type of objects to serialize into the list as
     * @param url The URL to hit
     */
    public static GetList<T>(toClass: new() => T, url: string, serviceCenter? : string) : Promise<undefined|T[]> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const sc = serviceCenter ? serviceCenter : Api.getServiceCenter();
    
        const responseProm = authenticatedApiCall(fullUrl, null, 'GET', sc);
        return responseProm
                    .then((resp: any) => {
                        const result = Mapper.deserializeList(toClass, resp);
                        return result;
                    })
                    .catch((err) => {
                        console.error("[API GET LIST] " + err);
                        throw err;
                    });
    
    }

    /**
     * Paged GET Api Helper
     * @param url The URL to hit
     */
    public static PagedGet<T>(toClass: new() => T, url: string, paginationInfo : PageData, serviceCenter? : string) 
        : Promise<undefined|PagedApiResult<T>> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const sc = Api.getServiceCenter();
        
        const responseProm = authenticatedApiCall(fullUrl, null, 'GET', sc);
        return responseProm
                .then((resp: any) => {
                    const result = Mapper.deserializeApiList(toClass, resp, paginationInfo);
                    return result;
                })
                .catch((err) => {
                    console.error("[API PAGED GET] " + err);
                    throw err;
                });
    
    }

    /**
     * POST Api Helper
     * @param url The URL to hit
     * @param data Data to pass along with the request
     */
    public static Post(url: string, data: any, contentType? : CONTENT_TYPES) : Promise<any> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
    
        const responseProm = authenticatedApiCall(fullUrl, data, 'POST', serviceCenter, contentType);
        return responseProm
                .then((resp: any) => {
                    return resp;
                })
                .catch((err) => {
                    console.error("[API POST] " + err);
                    throw err;
                });
    
    }

    /**
     * PUT Api Helper with typed return
     * @param url The URL to hit
     * @param data Data to pass along with the request
     */
    public static Put(url: string, data: any) : Promise<any> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
    
        const responseProm = authenticatedApiCall(fullUrl, data, 'PUT', serviceCenter);
        return responseProm
                .then((resp: any) => {
                    return resp;
                })
                .catch((err) => {
                    console.error("[API PUT] " + err);
                    throw err;
                });
    
    }

    /**
     * DELETE Api Helper
     * @param url The URL to hit
     */
    public static Delete(url: string, data: any) : Promise<any> {
    
        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
    
        const responseProm = authenticatedApiCall(fullUrl, data, 'DELETE', serviceCenter);
        return responseProm
                .then((resp: any) => {
                    return resp;
                })
                .catch((err) => {
                    console.error("[API DELETE] " + err);
                    throw err;
                });
    
    }

    public static GetBlob(url: string) : Promise<any> {

        const requestBase = AppSettings._apiHost;
        const fullUrl = `${ requestBase }${ url }`;
        const serviceCenter = Api.getServiceCenter();
    
        const responseProm = authenticatedBlobCall(fullUrl, serviceCenter);
        return responseProm
                .then((resp: any) => {
                    return resp;
                })
                .catch((err) => {
                    console.error("[API BLOB] " + err);
                    throw err;
                });

    }

    /**
     * Helper method to get the active service center out of the store
     */
    private static getServiceCenter() {
        const store = getApplicationStore();
        if (store) {
            const state = store.getState();
            return (state && state.userdata && state.userdata.activeServiceCenter) 
                    ? state.userdata.activeServiceCenter .name
                    : '';
        }
        
        return '';
    }

}

/**
 * Make an authenticated request to the API
 * @param url The URL to request
 * @param data The data to post/put (optional)
 * @param type The HTTP verb
 * @param serviceCenter The service center the request is for
 */
// export const authenticatedApiCall = (url:string, data:any, type:string, serviceCenter: string) => {
    
//     const request = {
//         scopes: [AppSettings._adScope]
//     } as Msal.AuthenticationParameters;

//     const msal = window.msal as Msal.UserAgentApplication;
//     return msal.acquireTokenSilent(request).then((response : Msal.AuthResponse) => { 

//         return fetch(url, {
//                 body: (data == null) ? null : JSON.stringify(data),
//                 headers: {
//                     'Authorization': `Bearer ${response.accessToken}`,
//                     'Content-Type': 'application/json',
//                     'X-CHI-SERVICECENTER': serviceCenter
//                 },
//                 method: type
//             })
//             .then(response => {
//                 return response.text()
//                     .then((text) => {
//                         const d = text ? JSON.parse(text) : {}
//                         if (!response.ok) {
//                             throw new RequestError(d, response.status, response.statusText);
//                         }
//                         return d;
//                     });
//             })
//             .catch(err => {
//                 console.log(err);
//                 throw err;
//             });
//     });
// }

/**
 * JSON.stringify replacer that writes the "type" of an object before its other properties.
 * The API tells which kind of stop, asset or shipment a body holds by its "type" and only looks for it as the first
 * property of the object.  JavaScript writes properties in the order they were first assigned, which for one without
 * an initial value is wherever the code gets round to setting it, so the order is put right as the body is written.
 */
export const typeFirst = (_key: string, value: any) => {

    if (value === null || typeof value !== 'object' || Array.isArray(value) || !('type' in value)) {
        return value;
    }

    const { type, ...others } = value;
    return { type, ...others };
};

/**
 * Make an authenticated request to the API
 * @param url The URL to request to
 * @param data The payload
 * @param type The HTTP request type
 * @param serviceCenter The service center for the header
 */
export const authenticatedApiCall = (url: string, data: any, type: string, serviceCenter: string, contentType?: CONTENT_TYPES) => {
    
    // Msal Request Object
    const request = {
        scopes: [AppSettings._adScope]
    } as Msal.AuthenticationParameters;

    let requestType = 'application/json';
    if (contentType) {
        requestType = contentType;
    }

    let bodyContent : any;
    if (requestType === 'multipart/form-data') {
        bodyContent = data;
    }
    else {
        if (data == null) {
            bodyContent = null;
        }
        else {
            const parsedData = Mapper.serialize(data);
            bodyContent = JSON.stringify(parsedData, typeFirst);
        }
    }

    // Create a funcition representing our request
    const makeRequest = (accessToken: string) => {

        const headerAry : any = {
            'Authorization': `Bearer ${accessToken}`,
            'X-CHI-SERVICECENTER': serviceCenter
        };

        if (requestType === 'application/json') {
            headerAry['Content-Type'] = requestType;
        }

        return fetch(url, {
            body: bodyContent,
            headers: headerAry,
            method: type
        })
        .then((response) => {
            return response.text()
                .then((text) => {
                    const d = text ? JSON.parse(text) : {};
                    if (!response.ok) {
                        throw new RequestError(d, response.status, response.statusText);
                    }
                    return d;
                });
        })
        .catch((err : RequestError) => {
            console.log(err);
            throw err;
        });
    };

    // Acquire the token and make the request, if the first one fails
    // then do a token popup to renew the token and make the request again
    const msal = window.msal as Msal.UserAgentApplication;
    return msal.acquireTokenSilent(request)
            .then((response : Msal.AuthResponse) => { 
                return makeRequest(response.accessToken);
            })
            .catch((error : any) => {

                console.log(error.errorMessage);

                // something went sideways
                // current thought it that this occurs because of the b2c 
                // migration we're undergoing and the fact that a lot of us
                // have b2c accounts in CHI and CG
                if (error && error.errorMessage) { 
                    if ((error.errorMessage.indexOf("User does not have an existing session and request prompt parameter has a value of 'None'.") > -1) ||
                        (error.errorMessage.indexOf('Token renewal operation failed due to timeout') > -1)) { 
                        location.reload();
                    }
                }

                if (error.name === "InteractionRequiredAuthError") {

                    msal.acquireTokenPopup(request)
                        .then((newResponse : Msal.AuthResponse) => {
                            return makeRequest(newResponse.accessToken);
                        }).catch((renewError: any) => {
                            msal.logout();
                        });

                }
                else {
                    console.log(error);
                    throw error;
                }

            });

};

/**
 * Make an authenticated request to the API
 * @param url The URL to request to
 * @param serviceCenter The service center for the header
 */
export const authenticatedBlobCall = (url: string, serviceCenter: string) => {
    
    // Msal Request Object
    const request = {
        scopes: [AppSettings._adScope]
    } as Msal.AuthenticationParameters;

    // Create a funcition representing our request
    const makeRequest = (accessToken: string) => {

        return fetch(url, {
            credentials: 'same-origin',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'X-CHI-SERVICECENTER': serviceCenter
            },
            method: 'GET',
            mode: 'cors',
            redirect: 'follow',
            referrer: 'no-referrer'
        })
        .then((response) => {
            if (response && response.status === 200) {
                return response.blob();
            }
            else if (response && response.status >= 400 && response.status < 500) {
                return response.json().then((data) => {
                    throw data;
                });
            }
            else {
                throw { 
                    errorCode: response.status, 
                    message: response.statusText
                };
            }
        })
        .catch((err) => {
            throw err;
        });
    };

    // Acquire the token and make the request, if the first one fails
    // then do a token popup to renew the token and make the request again
    const msal = window.msal as Msal.UserAgentApplication;
    return msal.acquireTokenSilent(request)
            .then((response : Msal.AuthResponse) => { 
                return makeRequest(response.accessToken);
            })
            .catch((error : any) => {

                console.log(error.errorMessage);

                // something went sideways
                // current thought it that this occurs because of the b2c 
                // migration we're undergoing and the fact that a lot of us
                // have b2c accounts in CHI and CG
                if (error && error.errorMessage) { 
                    if ((error.errorMessage.indexOf("User does not have an existing session and request prompt parameter has a value of 'None'.") > -1) ||
                        (error.errorMessage.indexOf('Token renewal operation failed due to timeout') > -1)) { 
                        location.reload();
                    }
                }

                if (error.name === "InteractionRequiredAuthError") {

                    msal.acquireTokenPopup(request)
                        .then((newResponse : Msal.AuthResponse) => {
                            return makeRequest(newResponse.accessToken);
                        }).catch((renewError: any) => {
                            msal.logout();
                        });

                }
                else {
                    console.log(error);
                    throw error;
                }

            });

};
